import { cn } from '@/lib/cn';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip, Plus, Receipt, Tags } from 'lucide-react';
import { add, format, parse, zero } from '@fint/money';
import { api, ApiError, qs } from '@/lib/api';
import { usePagedList } from '@/hooks/usePagedList';
import { ListFooter } from '@/features/common/ListFooter';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsPhone } from '@/hooks/useIsPhone';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { useToday } from '@/hooks/useOrgSettings';
import { formatDate } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import { activeOnly } from '@/features/common/Drafts';
import {
  Badge, Button, Card, CardFacts, CardLine, CardRow, Cards, ColourChip, EmptyState, ErrorState, Input, Money,
  SelectField, SkeletonTable, Textarea, TBody, TD, TH, THead, TR, Table,
  Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader,
} from '@/features/common/ui';
import { Attachments } from '@/features/common/Attachments';
import { ProjectSelect, TagPicker, useProjects, useTags, type TagOption } from '@/features/common/Classify';
import { categoryLabel } from '@/features/quick-add/quick-add';
import { AdvancedFilter, useAdvancedFilter } from '@/features/common/AdvancedFilter';
import { SpendingPanel } from './SpendingPanel';
import { ExpenseRefundsSheet } from './RefundSheets';
import { SearchChip, useSearchTerm } from '@/features/search/SearchChip';

interface ExpenseRow {
  id: string;
  date: string;
  categoryName: string;
  categoryColour: string;
  /** The category's picture, when it has one - see migration 0015. */
  categoryEmoji: string | null;
  totalAmount: string;
  currency: string;
  isBillable: boolean;
  isInvoiced: boolean;
  /**
   * Proposed, not spent. Claude drafts these from a photographed bill; until
   * somebody confirms one it is in no total, on no return, and no money has
   * left the account.
   */
  isDraft: boolean;
  /**
   * Money back on a charge (R-04): stored negative in the charge's category,
   * so every total nets. Shown as money back, with the charge one tap away -
   * a bare "-₹1,169" in a list of costs reads as a typo.
   */
  isRefund?: boolean;
  refundOfExpenseId?: string | null;
  /** How many refunds a charge has; "Refunds" shows only when there are some. */
  refundCount?: number;
  /** In a category paid between the owner's own books (Owner's salary). */
  betweenBooks?: boolean;
  accountName: string | null;
  notes: string | null;
  referenceNumber: string | null;
  /** Set when a receipt is attached. What expense_no_receipt reads. */
  receiptFileId: string | null;
  projectId: string | null;
  projectName: string | null;
  tags: TagOption[];
  /** Who was paid, when somebody has said (the Vendors page, or this row). */
  vendorId?: string | null;
  vendorName?: string | null;
}

interface Category { id: string; name: string; emoji?: string | null }
interface Account { id: string; name: string; type: string; balance: string; currency: string; isInvestment?: boolean }
interface TaxOption { id: string; name: string; percentage: string }

/** What `POST /expenses/bulk-classify` reports back. */
interface BulkSkip {
  expenseId: string;
  date: string;
  fields: string[];
  reason: 'period_locked' | 'tag_limit';
  message: string;
}

interface BulkResult {
  requested: number;
  updated: number;
  unchanged: number;
  skipped: BulkSkip[];
}

/** What `GET /expenses/ids` says about the whole filtered set. */
interface MatchingIds {
  ids: string[];
  matching: number;
  cap: number;
  truncated: boolean;
}

interface ExpenseFilterState {
  billable: string;
  projectId: string;
  categoryId: string;
  tagIds: string[];
  tagMatch: 'any' | 'all';
  /** The condition builder's `where` / `savedFilterId`, from the URL. */
  advanced: { where?: string; savedFilterId?: string };
  /** Text a search result opened the list with (`?q=`): notes or reference. */
  q: string;
}

/**
 * The query string behind the list AND behind "select all matching".
 *
 * ── WHY ONE BUILDER AND NOT TWO ──
 * The same reason the API has one predicate behind `list` and `matchingIds`. A
 * select-all that asked a slightly different question from the list would hand
 * somebody a selection containing rows they never saw - and then classify them
 * and report success. The two calls have to be the same question, so they are
 * built by the same function.
 *
 * `status: 'all'`, because this screen shows drafts: a draft nobody can see is
 * a draft nobody confirms.
 */
function expenseFilterQs(filters: ExpenseFilterState, cursor?: string): string {
  return `${qs({
    billable: filters.billable,
    status: 'all',
    q: filters.q || undefined,
    cursor,
    projectId: filters.projectId || undefined,
    categoryId: filters.categoryId || undefined,
    tagMatch: filters.tagIds.length > 1 ? filters.tagMatch : undefined,
    ...filters.advanced,
  })}${filters.tagIds.map((id) => `&tag=${encodeURIComponent(id)}`).join('')}`;
}

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'billable', label: 'Billable' },
  { value: 'unbilled', label: 'Not yet billed' },
  { value: 'non_billable', label: 'Not billable' },
] as const;

export function ExpensesPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  const queryClient = useQueryClient();
  const [billable, setBillable] = useState<string>('all');
  /**
   * The three filters the classification work made possible.
   *
   * `projectId` carries the literal `none` as a real option, because "what have
   * I not classified yet" is the question somebody asks on the day they adopt
   * projects - and it is the one that tells them their coverage is 23% rather
   * than leaving them to infer it from a report that does not match what they
   * expected.
   */
  const [projectId, setProjectId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagMatch, setTagMatch] = useState<'any' | 'all'>('any');
  const [composerOpen, setComposerOpen] = useState(false);
  // The condition builder lives in the URL (`?where=`), unlike the four
  // above - it is the one filter worth pasting to a colleague as it stands.
  const advanced = useAdvancedFilter();

  const canEdit = perms.can('expense', 'edit');
  const search = useSearchTerm();
  const filters = useMemo<ExpenseFilterState>(
    () => ({ billable, projectId, categoryId, tagIds, tagMatch, advanced: advanced.params, q: search.q }),
    [billable, projectId, categoryId, tagIds, tagMatch, advanced.params, search.q],
  );
  const filterKey = `${billable}|${projectId}|${categoryId}|${tagIds.join(',')}|${tagMatch}|${advanced.key}`;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  /**
   * Whether the selection means "everything this filter matches" or "these
   * particular rows".
   *
   * Held as a flag as well as a list because the two are DIFFERENT PROMISES and
   * the bar has to say which one is on screen. "All 47 matching" and "the 25
   * loaded on this page" is exactly the ambiguity that makes people afraid of
   * bulk actions, and the fear is well founded - it is the one control in the
   * product that can change four hundred records from a single click.
   *
   * The ids are still sent explicitly either way (see the schema's note on why
   * the API takes ids and not a filter).
   */
  const [allMatching, setAllMatching] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  /**
   * A filter change drops the selection, always.
   *
   * Keeping it would leave "all 47 matching" attached to a filter that now
   * matches something else, and the next Classify would act on rows chosen
   * under a question nobody asked any more.
   */
  useEffect(() => {
    setSelectedIds([]);
    setAllMatching(false);
    setBulkOpen(false);
  }, [filterKey]);


  /**
   * Turning a proposal into spend.
   *
   * The one button in the product that takes a figure Claude read off a
   * photograph and puts it in the books. Everything the draft was excluded
   * from - the spend total, the dashboard, input credit on a return, the
   * review engine - starts counting it, and the money leaves the account.
   * So it is a deliberate press by a person, and never anything else.
   */
  const confirm = useMutation({
    mutationFn: (expenseId: string) => api.post(`/expenses/${expenseId}/confirm`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] });
      // The dashboard and the bank balances both move the moment this lands.
      void queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['bank-accounts', org?.id] });
    },
  });
  // One row at a time. Several strips open at once is noise, not density.
  const [openReceipt, setOpenReceipt] = useState<string | null>(null);
  // The charge whose refunds are open - from the charge itself, or from one of
  // its refunds ("see the charge").
  const [refundsFor, setRefundsFor] = useState<{ id: string; label: string } | null>(null);

  const query = usePagedList<ExpenseRow, { total: string; scopedToSelf: boolean }>({
    key: ['expenses', org?.id, billable, projectId, categoryId, tagIds.join(','), tagMatch, advanced.key, search.q],
    /**
     * `status=all`, so drafts appear here.
     *
     * The API defaults to `recorded` - every caller that predates drafts is
     * asking about spend, and quietly folding proposals into their answers
     * would be the bug. This screen is the one place that wants both, because
     * a draft nobody can see is a draft nobody confirms.
     */
    path: (cursor) => `/expenses${expenseFilterQs(filters, cursor)}`,
    enabled: !!org?.id,
  });

  const currency = query.items[0]?.currency ?? 'INR';
  const loadedTotal = format(
    (query.data?.pages ?? []).reduce(
      (sum, page) => add(sum, parse(page.total ?? '0', currency)),
      zero(currency),
    ),
  );

  const loadedIds = query.items.map((expense) => expense.id);
  const allLoadedSelected = loadedIds.length > 0 && loadedIds.every((id) => selected.has(id));
  const someLoadedSelected = loadedIds.some((id) => selected.has(id));

  /**
   * How many the FILTER matches, which is not how many are on screen.
   *
   * Fetched only once something is selected: a screen nobody is bulk-editing
   * must not pay for a COUNT on every filter change.
   */
  const matching = useQuery({
    queryKey: ['expenses', org?.id, 'ids', filterKey],
    queryFn: () => api.get<MatchingIds>(`/expenses/ids${expenseFilterQs(filters)}`),
    enabled: !!org?.id && canEdit && selectedIds.length > 0,
  });

  const toggleOne = (id: string) => {
    // Touching one row ends "all matching": the selection is now a hand-made
    // list, and the bar must stop claiming otherwise.
    setAllMatching(false);
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((held) => held !== id) : [...current, id],
    );
  };

  const toggleAllLoaded = () => {
    if (allMatching) {
      // Coming down from "everything the filter matches": the tick means the
      // rows on screen, which is the only thing this checkbox has ever claimed
      // to control. Un-ticking it from here would otherwise leave a selection
      // made entirely of records that are not on the page.
      setAllMatching(false);
      setSelectedIds(loadedIds);
      return;
    }
    setSelectedIds((current) =>
      allLoadedSelected
        ? current.filter((id) => !loadedIds.includes(id))
        : Array.from(new Set([...current, ...loadedIds])),
    );
  };

  const bulk = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<BulkResult>('/expenses/bulk-classify', { expenseIds: selectedIds, ...body }),
    onSuccess: async (result) => {
      // The report is kept on screen until dismissed. A bulk action that
      // partially succeeded has something to say, and a toast that vanishes in
      // four seconds is not where it can say it.
      setBulkResult(result);
      setBulkOpen(false);
      setSelectedIds([]);
      setAllMatching(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] }),
        queryClient.invalidateQueries({ queryKey: ['project-report', org?.id] }),
        // A category change moves the dashboard's spend breakdown.
        queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] }),
      ]);
    },
  });

  return (
    <>
      <PageHeader
        title="Expenses"
        subtitle="What you have spent, and which account it came out of."
        actions={
          perms.can('expense', 'create') ? (
            <Button size="sm" onClick={() => setComposerOpen(true)}>
              <Plus className="size-4" aria-hidden />
              Record expense
            </Button>
          ) : null
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <SearchChip q={search.q} onClear={search.clear} />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setBillable(filter.value)}
                aria-pressed={billable === filter.value}
                className={
                  billable === filter.value
                    ? 'inline-flex min-w-11 items-center justify-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary touch:min-h-11 touch:py-0'
                    : 'inline-flex min-w-11 items-center justify-center rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent active:bg-accent touch:min-h-11 touch:py-0'
                }
              >
                {filter.label}
              </button>
            ))}
          </div>
          {query.items.length ? (
            <p className="text-sm text-muted-foreground">
              {/*
                Summed across the pages actually loaded, so the figure always
                describes the rows on screen. The API returns a per-page total,
                and showing page one's total beside three loaded pages would
                understate the spend by everything below the fold.
              */}
              {/*
                "Total of what is shown", not "Showing …". The list footer below
                also opens with "Showing", and two sentences beginning the same
                way but counting different things - rupees here, rows there -
                read as though one of them is wrong.
              */}
              Total of what is shown:{' '}
              <Money value={loadedTotal} currency={currency} className="font-medium text-foreground" />
            </p>
          ) : null}
        </div>

        <ExpenseFilters
          projectId={projectId}
          onProject={setProjectId}
          categoryId={categoryId}
          onCategory={setCategoryId}
          tagIds={tagIds}
          onTags={setTagIds}
          tagMatch={tagMatch}
          onTagMatch={setTagMatch}
        />

        <AdvancedFilter entityType="expense" value={advanced.value} onChange={advanced.set} />

        {/* The spend chart and the by-vendor split, for the person who came here asking where it is going. */}
        <SpendingPanel />

        {canEdit && selectedIds.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
            <p className="text-sm">
              <strong className="font-medium">
                {selectedIds.length} selected
              </strong>{' '}
              <span className="text-muted-foreground">
                {/*
                  Said in words, not implied by a checkbox state. Which of the
                  two selections is armed is the single thing somebody needs to
                  know before pressing Classify.
                */}
                {allMatching
                  ? '— every expense these filters match, including the ones below the fold.'
                  : '— only the rows you have ticked here, not the rest of the filter.'}
              </span>
            </p>

            {!allMatching && matching.data && matching.data.matching > selectedIds.length ? (
              matching.data.truncated ? (
                <p className="text-xs text-muted-foreground">
                  {matching.data.matching} expenses match these filters, and one bulk action takes{' '}
                  {matching.data.cap}. Narrow the filter — by month, or by account — and repeat.
                </p>
              ) : (
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => {
                    setSelectedIds(matching.data.ids);
                    setAllMatching(true);
                  }}
                >
                  Select all {matching.data.matching} matching these filters
                </Button>
              )
            ) : null}

            {allMatching ? (
              <Button
                size="sm"
                variant="link"
                onClick={() => {
                  setAllMatching(false);
                  setSelectedIds(loadedIds);
                }}
              >
                Select only the {loadedIds.length} shown
              </Button>
            ) : null}

            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" onClick={() => setBulkOpen((open) => !open)} aria-expanded={bulkOpen}>
                <Tags className="size-4" aria-hidden />
                Classify {selectedIds.length}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedIds([]);
                  setAllMatching(false);
                  setBulkOpen(false);
                }}
              >
                Clear
              </Button>
            </div>
          </div>
        ) : null}

        {bulkOpen && canEdit ? (
          <BulkClassifyPanel
            count={selectedIds.length}
            allMatching={allMatching}
            pending={bulk.isPending}
            error={bulk.error instanceof ApiError ? bulk.error : undefined}
            onApply={(body) => bulk.mutate(body)}
            onClose={() => setBulkOpen(false)}
          />
        ) : null}

        {bulkResult ? (
          <BulkResultReport result={bulkResult} onDismiss={() => setBulkResult(null)} />
        ) : null}


        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : query.isLoading ? (
            <SkeletonTable rows={6} cols={5} />
          ) : query.items.length ? (
            <>
            {isPhone ? (
            <Cards>
              {query.items.map((expense) => (
                <CardRow key={expense.id} className="group">
                  <CardLine
                    title={categoryLabel(expense.categoryName, expense.categoryEmoji)}
                    meta={expense.notes ?? expense.accountName ?? undefined}
                    value={<ExpenseAmount expense={expense} />}
                  />
                  <CardFacts>
                    {canEdit ? (
                      <label className="-ml-1 flex min-h-11 items-center gap-1.5 px-1">
                        <input
                          type="checkbox"
                          className="size-4 rounded border-input"
                          checked={selected.has(expense.id)}
                          onChange={() => toggleOne(expense.id)}
                        />
                        <span>Select</span>
                      </label>
                    ) : null}
                    <span>{formatDate(expense.date)}</span>
                    {expense.isDraft ? <Badge variant="warning">Not recorded yet</Badge> : null}
                    {expense.isRefund ? <Badge variant="success">Refund</Badge> : null}
                    {expense.betweenBooks ? (
                      <Badge variant="neutral" title="Paid to your other book. The Overview across books cancels it against the income there.">
                        Between your books
                      </Badge>
                    ) : null}
                    {expense.isInvoiced ? (
                      <Badge variant="success">Invoiced</Badge>
                    ) : expense.isBillable ? (
                      <Badge variant="warning">Billable</Badge>
                    ) : null}
                    <RefundsButton expense={expense} onOpen={setRefundsFor} />
                    {/* Attaching a receipt is the ONE thing most likely to be
                        done on a phone - the photo is already on it - so the
                        control is a full tap target rather than a table cell. */}
                    <button
                      type="button"
                      onClick={() => setOpenReceipt(openReceipt === expense.id ? null : expense.id)}
                      aria-expanded={openReceipt === expense.id}
                      className="-mx-1 flex min-h-9 items-center gap-1.5 rounded px-1 text-xs hover:bg-accent active:bg-accent touch:min-h-11"
                    >
                      <Paperclip className="size-3.5" aria-hidden />
                      {expense.receiptFileId ? (
                        <span className="text-success">Receipt attached</span>
                      ) : (
                        <span className="text-muted-foreground">Add receipt</span>
                      )}
                    </button>
                  </CardFacts>
                  {expense.isDraft && perms.can('expense', 'edit') ? (
                    <div className="mt-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2">
                      <p className="text-xs text-muted-foreground">
                        Drafted from a bill. It is in no total and no money has left the account yet.
                      </p>
                      <Button
                        size="sm"
                        className="mt-2 w-full"
                        loading={confirm.isPending && confirm.variables === expense.id}
                        onClick={() => confirm.mutate(expense.id)}
                      >
                        Confirm this expense
                      </Button>
                    </div>
                  ) : null}
                  {openReceipt === expense.id ? (
                    <div className="mt-3">
                      <Attachments
                        ownerType="expense"
                        ownerId={expense.id}
                        title={`Receipt for ${expense.categoryName ?? 'this expense'}`}
                        emptyHint="Take a photo, or pick one from your camera roll. It is compressed on the way in, so send the full-size original."
                      />
                    </div>
                  ) : null}
                </CardRow>
              ))}
            </Cards>
            ) : (

                        <Table>
              <THead>
                <TR>
                  {canEdit ? (
                    <TH className="w-8">
                      {/*
                        This ticks the rows that are LOADED, and its name says
                        so. Selecting everything the filter matches is a second,
                        deliberate click in the bar above - because "all 47" and
                        "the 25 on this page" are different promises, and a
                        checkbox cannot make the distinction on its own.
                      */}
                      {/* A 44px label around a 24px box: the cell is the target, and a
                          finger that lands beside the box still ticks it. */}
                      <label className="-m-2.5 flex size-11 cursor-pointer items-center justify-center">
                        <input
                          type="checkbox"
                          className="size-4 rounded border-input"
                          aria-label={`Select the ${loadedIds.length} expenses shown`}
                          checked={allLoadedSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someLoadedSelected && !allLoadedSelected;
                          }}
                          onChange={toggleAllLoaded}
                        />
                      </label>
                    </TH>
                  ) : null}
                  <TH>Date</TH>
                  <TH>Category</TH>
                  <TH>Paid from</TH>
                  <TH>Notes</TH>
                  <TH>Project &amp; tags</TH>
                  <TH numeric>Amount</TH>
                  <TH>Receipt</TH>
                </TR>
              </THead>
              <TBody>
                {query.items.map((expense) => (
                  <Fragment key={expense.id}>
                    <TR className="group">
                      {canEdit ? (
                        <TD>
                          {/* A 44px label around a 24px box: the cell is the target, and a
                              finger that lands beside the box still ticks it. */}
                          <label className="-m-2.5 flex size-11 cursor-pointer items-center justify-center">
                            <input
                              type="checkbox"
                              className="size-4 rounded border-input"
                              aria-label={`Select the expense dated ${formatDate(expense.date)}`}
                              checked={selected.has(expense.id)}
                              onChange={() => toggleOne(expense.id)}
                            />
                          </label>
                        </TD>
                      ) : null}
                      <TD className="whitespace-nowrap text-muted-foreground">{formatDate(expense.date)}</TD>
                      <TD>
                        <div className="flex items-center gap-2">
                          <ColourChip
                            colour={expense.categoryColour}
                            emoji={expense.categoryEmoji}
                            name={expense.categoryName}
                          />
                          {expense.isDraft ? <Badge variant="warning">Not recorded yet</Badge> : null}
                          {expense.isRefund ? <Badge variant="success">Refund</Badge> : null}
                    {expense.betweenBooks ? (
                      <Badge variant="neutral" title="Paid to your other book. The Overview across books cancels it against the income there.">
                        Between your books
                      </Badge>
                    ) : null}
                          {expense.isInvoiced ? (
                            <Badge variant="success">Invoiced</Badge>
                          ) : expense.isBillable ? (
                            <Badge variant="warning">Billable</Badge>
                          ) : null}
                          <RefundsButton expense={expense} onOpen={setRefundsFor} />
                        </div>
                        {expense.isDraft && perms.can('expense', 'edit') ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="mt-1.5"
                            loading={confirm.isPending && confirm.variables === expense.id}
                            onClick={() => confirm.mutate(expense.id)}
                          >
                            Confirm
                          </Button>
                        ) : null}
                      </TD>
                      <TD className="text-muted-foreground">{expense.accountName ?? '—'}</TD>
                      <TD className="max-w-[200px] truncate text-muted-foreground">{expense.notes ?? '—'}</TD>
                      <TD>
                        <ClassifyCell expense={expense} canEdit={perms.can('expense', 'edit')} />
                      </TD>
                      <TD numeric>
                        {/* A draft's amount is dimmed rather than hidden: the
                            figure is the thing being confirmed, but it is not
                            in the total above and must not look as though it
                            is. */}
                        <ExpenseAmount expense={expense} />
                      </TD>
                      <TD>
                        {/* The state is visible without expanding anything.
                            "Has a receipt" is the question the review engine
                            asks about every large expense, so it belongs in the
                            list rather than one click away. */}
                        <button
                          type="button"
                          onClick={() => setOpenReceipt(openReceipt === expense.id ? null : expense.id)}
                          aria-expanded={openReceipt === expense.id}
                          className="flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs hover:bg-accent active:bg-accent"
                          data-tap-exempt="text button in a table cell; the hit area is drawn around it"
                        >
                          <Paperclip className="size-3.5" aria-hidden />
                          {expense.receiptFileId ? (
                            <span className="text-success">Attached</span>
                          ) : (
                            <span className="text-muted-foreground">Add</span>
                          )}
                        </button>
                      </TD>
                    </TR>
                    {openReceipt === expense.id ? (
                      <TR>
                        <TD colSpan={canEdit ? 8 : 7} className="bg-muted/20">
                          <Attachments
                            ownerType="expense"
                            ownerId={expense.id}
                            title={`Receipt for ${expense.categoryName ?? 'this expense'}`}
                            emptyHint="Drag the photo here, or paste a screenshot. It is compressed on the way in, so send the full-size original."
                          />
                        </TD>
                      </TR>
                    ) : null}
                  </Fragment>
                ))}
              </TBody>
            </Table>
            )}
            <ListFooter query={query} noun="expenses" />
            </>
          ) : (
            <EmptyState
              icon={Receipt}
              title={billable === 'all' && !advanced.active ? 'No expenses yet' : 'Nothing matches that filter'}
              description={
                billable === 'all' && !advanced.active
                  ? 'Record what you spend and it comes straight off the account you paid from.'
                  : undefined
              }
              action={
                billable === 'all' && !advanced.active && perms.can('expense', 'create') ? (
                  // "your first", not a second "Record expense": the header
                  // already has one. Two controls announced identically that do
                  // the same thing read as a duplicate to a screen reader, and
                  // every locator that ever targeted the header had to say
                  // `.first()` to avoid this one - which is a test papering over
                  // the same ambiguity a person would meet.
                  <Button size="sm" onClick={() => setComposerOpen(true)}>
                    Record your first expense
                  </Button>
                ) : null
              }
            />
          )}
        </Card>

        {query.first?.scopedToSelf ? (
          <p className="text-xs text-muted-foreground">
            You are seeing only the expenses you filed. An administrator sees everyone&rsquo;s.
          </p>
        ) : null}
      </div>

      {refundsFor ? (
        <ExpenseRefundsSheet
          expenseId={refundsFor.id}
          categoryLabel={refundsFor.label}
          canRecord={perms.can('expense', 'create')}
          onClose={() => setRefundsFor(null)}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] });
            // A refund puts money into the account and takes it off a category.
            void queryClient.invalidateQueries({ queryKey: ['banking', org?.id] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] });
          }}
        />
      ) : null}

      {composerOpen ? (
        <ExpenseComposer
          onClose={() => setComposerOpen(false)}
          onCreated={() => {
            setComposerOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] });
            // An expense moves an account balance, so both of these change too.
            void queryClient.invalidateQueries({ queryKey: ['banking', org?.id] });
            void queryClient.invalidateQueries({ queryKey: ['dashboard', org?.id] });
          }}
        />
      ) : null}
    </>
  );
}

/**
 * The amount as a person reads it: a cost as a cost, a refund as money BACK.
 * A draft's figure is dimmed rather than hidden - it is the thing being
 * confirmed, but it is not in the total above and must not look as though it is.
 */
function ExpenseAmount({ expense }: { expense: ExpenseRow }) {
  if (expense.isRefund) {
    return (
      <span className="tabular text-success" data-testid="expense-refund-amount">
        <Money value={expense.totalAmount.replace(/^-/, '')} currency={expense.currency} /> back
      </span>
    );
  }
  return (
    <Money
      value={expense.totalAmount}
      currency={expense.currency}
      className={expense.isDraft ? 'text-muted-foreground' : undefined}
    />
  );
}

/**
 * The way to a charge's refunds: "Refunds" on a recorded charge, "See the
 * charge" on a refund. A draft has nothing recorded to refund yet.
 */
function RefundsButton({
  expense,
  onOpen,
}: {
  expense: ExpenseRow;
  onOpen: (target: { id: string; label: string }) => void;
}) {
  if (expense.isDraft) return null;
  const target = expense.isRefund ? expense.refundOfExpenseId : expense.id;
  if (!target) return null;
  const count = expense.refundCount ?? 0;
  /**
   * A charge WITH money back says so ("1 refund"), always. One without offers
   * "Add refund" only while the row is hovered or focused: shown on every row,
   * the bare word sat beside the category like a label, and a column of
   * salary payments each appeared to say "Refunds". On a touch screen, with
   * no hover, it is always there - the one way to reach it from the list.
   */
  const quiet = !expense.isRefund && count === 0;
  const label = expense.isRefund ? 'See the charge' : count > 0 ? `${count} refund${count === 1 ? '' : 's'}` : 'Add refund';
  return (
    <button
      type="button"
      onClick={() => onOpen({ id: target, label: categoryLabel(expense.categoryName, expense.categoryEmoji) })}
      className={cn(
        'rounded px-1 text-xs text-muted-foreground underline-offset-2 hover:bg-accent hover:underline touch:min-h-11',
        // Hidden only where there is a hover to bring it back; group-hover
        // is itself (hover: hover)-only, so a touch screen never hides it.
        quiet && '[@media(hover:hover)]:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100',
      )}
      data-testid={expense.isRefund ? 'expense-see-charge' : 'expense-refunds-open'}
      data-tap-exempt="text button beside the category badges; the hit area is drawn around it"
    >
      {label}
    </button>
  );
}

function ExpenseComposer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const today = useToday();
  const [form, setForm] = useState({
    // The org's today, not the browser's UTC - see useToday. An expense filed
    // at 01:00 IST used to be dated yesterday, which at a month boundary puts
    // it in the wrong period and, if that period is locked, refuses the save.
    date: today,
    categoryId: '',
    amount: '',
    paidThroughAccountId: '',
    referenceNumber: '',
    taxId: '',
    notes: '',
    isBillable: false,
    customerId: '',
    projectId: '',
  });
  // Chips rather than ids, so removing one does not need a second lookup to
  // render what was removed.
  const [tags, setTags] = useState<TagOption[]>([]);
  // See `TagPicker.onBusyChange`: submitting while a tag is still being created
  // writes the set without it and reports success.
  const [tagsBusy, setTagsBusy] = useState(false);

  const categories = useQuery({
    queryKey: ['expense-categories', org?.id],
    queryFn: () => api.get<{ items: Category[] }>('/expenses/categories'),
    enabled: !!org?.id,
  });
  const accounts = useQuery({
    queryKey: ['banking', org?.id, 'accounts'],
    queryFn: () => api.get<{ items: Account[] }>('/banking/accounts'),
    enabled: !!org?.id,
  });
  const customers = useQuery({
    queryKey: ['customers', org?.id, 'picker'],
    queryFn: () => api.get<{ items: Array<{ id: string; displayName: string }> }>('/customers?status=active&limit=200').then(activeOnly),
    enabled: !!org?.id,
  });
  const taxes = useQuery({
    queryKey: ['taxes', org?.id],
    queryFn: () => api.get<{ items: TaxOption[] }>('/taxes'),
    enabled: !!org?.id,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post('/expenses', {
        date: form.date,
        categoryId: form.categoryId,
        amount: form.amount,
        paidThroughAccountId: form.paidThroughAccountId,
        referenceNumber: form.referenceNumber || undefined,
        // The rate the bill shows. `taxInclusive` is left to its default
        // (true): the amount typed off a receipt is the gross one.
        taxId: form.taxId || undefined,
        notes: form.notes || undefined,
        isBillable: form.isBillable,
        customerId: form.isBillable ? form.customerId || undefined : undefined,
        // An empty select is "not on a project", which is a value rather than
        // an omission - but the create schema takes null for it either way.
        projectId: form.projectId || null,
        tagIds: tags.length > 0 ? tags.map((tag) => tag.id) : undefined,
      }),
    onSuccess: onCreated,
  });

  const error = create.error instanceof ApiError ? create.error : undefined;
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Sheet label="Record expense" onClose={onClose}>
      <SheetHeader title="Record expense" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <SheetBody>
          <Input label="Date" type="date" required value={form.date} onChange={set('date')} />

          <div className="flex flex-col gap-1.5">
            <span className="flex items-center text-sm font-medium">
                <label htmlFor="expense-category">Category</label>
                <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
              </span>
            <select
              id="expense-category"
              required
              value={form.categoryId}
              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select a category…</option>
              {categories.data?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {categoryLabel(c.name, c.emoji)}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Amount"
            required
            inputMode="decimal"
            value={form.amount}
            onChange={set('amount')}
            error={error?.fieldError('amount')}
            hint="What the receipt says, tax included."
          />

          <div className="flex flex-col gap-1.5">
            <span className="flex items-center text-sm font-medium">
                <label htmlFor="expense-account">Paid from</label>
                <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
              </span>
            <select
              id="expense-account"
              required
              value={form.paidThroughAccountId}
              onChange={(e) => setForm((f) => ({ ...f, paidThroughAccountId: e.target.value }))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Select an account…</option>
              {/* An FD or fund is not where a bill is paid from - unless this
                  expense already says it was, which an edit must still show. */}
              {accounts.data?.items
                .filter((a) => !a.isInvestment || a.id === form.paidThroughAccountId)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
            </select>
            <p className="text-xs text-muted-foreground">This comes straight off that account&rsquo;s balance.</p>
          </div>

          <Input label="Reference" value={form.referenceNumber} onChange={set('referenceNumber')} hint="The bill number as printed." />
          {/* The two things a matched bill adds to a statement row (0018) -
              its number above, its GST rate here - so a person filing by
              hand can say what the agent would otherwise propose. Offered
              only once the book has rates: a book without GST has nothing
              to pick and no reason to see the field. */}
          {taxes.data?.items.length ? (
            <SelectField
              id="expense-tax"
              label="GST rate"
              value={form.taxId}
              onChange={(taxId) => setForm((f) => ({ ...f, taxId }))}
              error={error?.fieldError('taxId')}
            >
              <option value="">None on the bill</option>
              {taxes.data.items.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </SelectField>
          ) : null}
          <Textarea label="Notes" value={form.notes} onChange={set('notes')} />

          {/*
            After the money and before the billing decisions, because that is
            what they are: classification, not pricing. An expense may go on ANY
            project including an internal one - it is only documents that an
            internal project may not hold.
          */}
          <ProjectSelect
            id="expense-project"
            value={form.projectId}
            onChange={(projectId) => setForm((f) => ({ ...f, projectId }))}
            hint="Which job this was for. Leave it blank for general spending."
          />
          <TagPicker id="expense-tags" value={tags} onChange={setTags} onBusyChange={setTagsBusy} />

          <div className="flex items-start gap-2 text-sm">
            <input
              id="expense-billable"
              type="checkbox"
              checked={form.isBillable}
              onChange={(e) => setForm((f) => ({ ...f, isBillable: e.target.checked }))}
              aria-describedby="expense-billable-hint"
              className="mt-0.5"
            />
            <div>
              <label htmlFor="expense-billable">Bill this to a customer</label>
              <p id="expense-billable-hint" className="text-xs text-muted-foreground">
                It can then be added to one of their invoices.
              </p>
            </div>
          </div>

          {form.isBillable ? (
            <div className="flex flex-col gap-1.5">
              <span className="flex items-center text-sm font-medium">
                  <label htmlFor="expense-customer">Customer</label>
                  <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
                </span>
              <select
                id="expense-customer"
                value={form.customerId}
                onChange={(e) => setForm((f) => ({ ...f, customerId: e.target.value }))}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
              >
                <option value="">Select a customer…</option>
                {customers.data?.items.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
              {error?.fieldError('customerId') ? (
                <p className="text-xs text-destructive">{error.fieldError('customerId')}</p>
              ) : null}
            </div>
          ) : null}

          {error && !error.errors?.length ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter>
          <Button type="submit" loading={create.isPending} disabled={tagsBusy}>
            Record expense
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

/**
 * Project, category and tag, as controls rather than as query parameters
 * somebody has to know about.
 *
 * ── WHY EACH ONE IS NAMED "FILTER BY …" ──
 * Three selects in one row called "Project", "Category" and "Tags" sit next to
 * a composer whose fields have exactly those names. To a screen reader moving
 * through the page that is six controls and three names; the house rule about
 * overlapping accessible names exists for precisely this row.
 *
 * The tag filter offers Any/All only once two tags are chosen, because with one
 * tag the two modes are the same question and a control that cannot change the
 * answer is a control that costs a decision.
 */
function ExpenseFilters({
  projectId,
  onProject,
  categoryId,
  onCategory,
  tagIds,
  onTags,
  tagMatch,
  onTagMatch,
}: {
  projectId: string;
  onProject: (value: string) => void;
  categoryId: string;
  onCategory: (value: string) => void;
  tagIds: string[];
  onTags: (value: string[]) => void;
  tagMatch: 'any' | 'all';
  onTagMatch: (value: 'any' | 'all') => void;
}) {
  const org = useActiveOrg();
  const projects = useProjects();
  const tags = useTags();
  const categories = useQuery({
    queryKey: ['expense-categories', org?.id],
    queryFn: () => api.get<{ items: Category[] }>('/expenses/categories'),
    enabled: !!org?.id,
  });

  const active = !!projectId || !!categoryId || tagIds.length > 0;
  const chosenTags = (tags.data?.items ?? []).filter((tag) => tagIds.includes(tag.id));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Filter by project"
          value={projectId}
          onChange={(event) => onProject(event.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Project: all</option>
          <option value="none">Not on a project</option>
          {(projects.data?.items ?? []).map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by category"
          value={categoryId}
          onChange={(event) => onCategory(event.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Category: all</option>
          {(categories.data?.items ?? []).map((category) => (
            <option key={category.id} value={category.id}>
              {categoryLabel(category.name, category.emoji)}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by tag"
          value=""
          onChange={(event) => {
            const id = event.target.value;
            if (id && !tagIds.includes(id)) onTags([...tagIds, id]);
          }}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">
            {tagIds.length === 0 ? 'Tags: all' : `Tags: ${tagIds.length} selected`}
          </option>
          {(tags.data?.items ?? [])
            .filter((tag) => !tagIds.includes(tag.id))
            .map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
        </select>

        {tagIds.length > 1 ? (
          <div role="group" aria-label="Match tags" className="flex gap-1">
            {(
              [
                ['any', 'Any of these'],
                ['all', 'All of these'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={tagMatch === key}
                onClick={() => onTagMatch(key)}
                className={
                  tagMatch === key
                    ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                    : 'rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent'
                }
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {active ? (
          <button
            type="button"
            onClick={() => {
              onProject('');
              onCategory('');
              onTags([]);
            }}
            className="text-xs text-primary hover:underline"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      {/* The chosen tags render as removable chips below the row, so the
          filter is visible without opening the select that set it. */}
      {chosenTags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {chosenTags.map((tag) => (
            <ColourChip
              key={tag.id}
              colour={tag.colour}
              name={tag.name}
              onRemove={() => onTags(tagIds.filter((id) => id !== tag.id))}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Project and tags on the row, editable without opening the composer.
 *
 * ── WHY THIS EXISTS SEPARATELY FROM THE COMPOSER ──
 * A record dated inside a FILED period refuses `PATCH` - correctly, because the
 * general update path can move a date or an amount. Classification cannot, and
 * is deliberately exempt, so it has its own narrow endpoints. But an exemption
 * nobody can reach is not an exemption: without a control outside the form,
 * "classify last year's spend by project" - the single most likely use of the
 * whole feature - would still be impossible for exactly the months people want
 * it for.
 *
 * So this writes through `PUT /expenses/:id/tags` and `PUT /expenses/:id/project`,
 * which can touch nothing else, and never through the record `PATCH`.
 */
function ClassifyCell({ expense, canEdit }: { expense: ExpenseRow; canEdit: boolean }) {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState<TagOption[]>(expense.tags);
  const [projectId, setProjectId] = useState(expense.projectId ?? '');
  const [vendorId, setVendorId] = useState(expense.vendorId ?? '');
  const [tagsBusy, setTagsBusy] = useState(false);
  // Read only while the popover is open: the vendor list is for the picker.
  const vendors = useQuery({
    queryKey: ['vendors', org?.id, 'picker'],
    queryFn: () => api.get<{ items: Array<{ id: string; displayName: string }> }>('/vendors?limit=200&sort=name'),
    enabled: open && !!org?.id,
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: async () => {
      // Two narrow writes rather than one wide one. Each is idempotent, so a
      // retry after a half-failure is safe.
      await api.put(`/expenses/${expense.id}/tags`, { tagIds: tags.map((tag) => tag.id) });
      await api.put(`/expenses/${expense.id}/project`, { projectId: projectId || null });
      // Who was paid: through bulk-classify, which - like the two above -
      // writes a label and nothing else, so a filed period allows it.
      if ((vendorId || null) !== (expense.vendorId ?? null)) {
        await api.post('/expenses/bulk-classify', { expenseIds: [expense.id], setVendor: { vendorId: vendorId || null } });
      }
    },
    /**
     * AWAITED, and the popover closes only afterwards.
     *
     * It used to close first and fire the invalidation unawaited, so the
     * control reported success while the row behind it still showed the old
     * classification - briefly, with nothing on screen admitting the gap.
     * react-query keeps the mutation pending until this resolves, so the Save
     * button stays busy until the row is actually showing what was saved, which
     * is what "saved" should mean.
     */
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] }),
        queryClient.invalidateQueries({ queryKey: ['project-report', org?.id] }),
        queryClient.invalidateQueries({ queryKey: ['vendors', org?.id] }),
      ]);
      setOpen(false);
    },
  });

  /**
   * Named for the record it classifies, and deliberately NOT starting with
   * "Add" - the receipt cell two columns over is already "Add receipt", and two
   * buttons on one row whose names begin the same way are two controls a screen
   * reader cannot tell apart, and two a locator picks between by position.
   */
  const label =
    expense.tags.length > 0
      ? `Project and tags: ${expense.tags.map((tag) => tag.name).join(', ')}. Edit`
      : 'Set the vendor, project and tags';

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => canEdit && setOpen((current) => !current)}
        aria-label={label}
        aria-expanded={open}
        disabled={!canEdit}
        className="flex flex-wrap items-center gap-1 text-left disabled:cursor-default"
        data-tap-exempt="text button in a table cell; the hit area is drawn around it"
      >
        {expense.vendorName ? (
          <span className="text-xs font-medium" data-testid="expense-vendor">{expense.vendorName}</span>
        ) : null}
        {expense.projectName ? (
          <span className="text-xs text-muted-foreground">{expense.projectName}</span>
        ) : null}
        {expense.tags.slice(0, 2).map((tag) => (
          <ColourChip key={tag.id} colour={tag.colour} name={tag.name} />
        ))}
        {expense.tags.length > 2 ? (
          <span className="text-xs text-muted-foreground">+{expense.tags.length - 2}</span>
        ) : null}
        {!expense.vendorName && !expense.projectName && expense.tags.length === 0 ? (
          <span className="text-xs text-muted-foreground">{canEdit ? 'Classify…' : '—'}</span>
        ) : null}
      </button>

      {open ? (
        <div className="mt-2 space-y-3 rounded-md border border-border bg-popover p-3 shadow-md">
          <div className="space-y-1">
            <label htmlFor={`row-vendor-${expense.id}`} className="text-sm font-medium">
              Vendor
            </label>
            <select
              id={`row-vendor-${expense.id}`}
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              data-testid="row-vendor"
              className="min-h-11 w-full rounded-md border border-input bg-background px-2 text-base shadow-sm sm:min-h-9 sm:text-sm"
            >
              <option value="">No vendor</option>
              {(vendors.data?.items ?? []).map((vendor) => (
                <option key={vendor.id} value={vendor.id}>
                  {vendor.displayName}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">Who was paid. Add one on the Vendors page.</p>
          </div>
          <ProjectSelect
            id={`row-project-${expense.id}`}
            value={projectId}
            onChange={setProjectId}
            hint="Which job this was for."
          />
          <TagPicker
            id={`row-tags-${expense.id}`}
            value={tags}
            onChange={setTags}
            onBusyChange={setTagsBusy}
          />
          {save.error instanceof ApiError ? (
            <p role="alert" className="text-sm text-destructive">
              {save.error.message}
            </p>
          ) : null}
          <div className="flex gap-2">
            {/*
              Disabled while a tag is still being created: saving over it would
              write the set WITHOUT it and report success. See `onBusyChange`.
            */}
            <Button
              size="sm"
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={tagsBusy}
            >
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
          {/*
            Said here rather than discovered on a refusal: this is the one place
            in the product where a record inside a filed period can still be
            edited, and somebody who knows the lock exists needs telling why
            this is allowed.
          */}
          <p className="text-xs text-muted-foreground">
            Labels and the project can change even in a filed period. The date and the amounts cannot.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the batch did, and precisely what it would not do.
 *
 * ── WHY THE SKIPPED ROWS ARE LISTED AND NOT COUNTED ──
 * A bulk action across a filed month is the ordinary case: the person
 * classifying last year's spend is by definition working inside periods that
 * have been filed. Tags and the project go through; the category does not. If
 * that were reported as "480 of 500 updated" the user would read a success and
 * carry on believing every category changed - and would find out months later,
 * from a figure that did not move. So each refusal names the record's date and
 * the lock's own reason, which is what tells somebody what to do instead.
 */
function BulkResultReport({ result, onDismiss }: { result: BulkResult; onDismiss: () => void }) {
  const shown = result.skipped.slice(0, 8);
  return (
    <div role="status" className="space-y-2 rounded-md border border-border bg-muted/30 px-3 py-2">
      <p className="text-sm">
        <strong className="font-medium">
          {result.updated === 0
            ? 'Nothing changed.'
            : `Classified ${result.updated} expense${result.updated === 1 ? '' : 's'}.`}
        </strong>{' '}
        {result.unchanged > 0 ? (
          <span className="text-muted-foreground">
            {result.unchanged} already carried what you asked for.
          </span>
        ) : null}
      </p>

      {result.skipped.length > 0 ? (
        <div className="space-y-1">
          <p className="text-sm text-warning">
            {result.skipped.length} left as {result.skipped.length === 1 ? 'it was' : 'they were'}:
          </p>
          <ul className="space-y-1 text-xs text-muted-foreground">
            {shown.map((skip) => (
              <li key={`${skip.expenseId}-${skip.reason}`}>
                <span className="text-foreground">{formatDate(skip.date)}</span> — kept its{' '}
                {skip.fields.join(' and ')}. {skip.message}
              </li>
            ))}
          </ul>
          {result.skipped.length > shown.length ? (
            <p className="text-xs text-muted-foreground">
              …and {result.skipped.length - shown.length} more for the same reason. They are in the
              activity log with the record each one belongs to.
            </p>
          ) : null}
        </div>
      ) : null}

      <Button size="sm" variant="ghost" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}

/**
 * What to apply to the selection.
 *
 * ── WHY TAGS ARE ADD AND REMOVE, WHERE ONE RECORD REPLACES ──
 * The row-level control replaces the whole set, which is right when somebody is
 * looking at that record's chips. Across a selection they are not: replacing
 * would strip every tag the selected rows carry that is not in this box -
 * "Reimbursable" on three of forty rows, gone, reported as success. A delta is
 * the only shape that cannot destroy what was not on screen.
 *
 * ── WHY THE PROJECT NEEDS A CHECKBOX IN FRONT OF IT ──
 * An empty project select means "not on a project", which is a real value here
 * and not the absence of one. Without the tick, opening this panel to add a tag
 * and pressing Apply would take forty expenses OFF their projects. So setting
 * the project is something you have to say you are doing.
 */
function BulkClassifyPanel({
  count,
  allMatching,
  pending,
  error,
  onApply,
  onClose,
}: {
  count: number;
  allMatching: boolean;
  pending: boolean;
  error?: ApiError;
  onApply: (body: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const org = useActiveOrg();
  const tags = useTags();
  const [changeProject, setChangeProject] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [addTagIds, setAddTagIds] = useState<string[]>([]);
  const [removeTagIds, setRemoveTagIds] = useState<string[]>([]);

  const categories = useQuery({
    queryKey: ['expense-categories', org?.id],
    queryFn: () => api.get<{ items: Category[] }>('/expenses/categories'),
    enabled: !!org?.id,
  });

  const chosen = (ids: string[]) => (tags.data?.items ?? []).filter((tag) => ids.includes(tag.id));
  const acts = changeProject || !!categoryId || addTagIds.length > 0 || removeTagIds.length > 0;

  return (
    <form
      className="space-y-4 rounded-md border border-border bg-card p-4"
      aria-label={`Classify ${count} expenses`}
      onSubmit={(event) => {
        event.preventDefault();
        onApply({
          setProject: changeProject ? { projectId: projectId || null } : undefined,
          categoryId: categoryId || undefined,
          addTagIds: addTagIds.length > 0 ? addTagIds : undefined,
          removeTagIds: removeTagIds.length > 0 ? removeTagIds : undefined,
        });
      }}
    >
      <p className="text-sm font-medium">
        Classify {count} expense{count === 1 ? '' : 's'}
        <span className="ml-1 font-normal text-muted-foreground">
          {allMatching ? '(everything these filters match)' : '(the rows you ticked)'}
        </span>
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 rounded border-input"
              checked={changeProject}
              onChange={(event) => setChangeProject(event.target.checked)}
            />
            Set the project
          </label>
          {changeProject ? (
            <ProjectSelect
              id="bulk-project"
              value={projectId}
              onChange={setProjectId}
              hint="Applies to every selected expense. “Not on a project” takes them off one."
            />
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="bulk-category" className="text-sm font-medium">
            Category
          </label>
          <select
            id="bulk-category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            aria-describedby="bulk-category-hint"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
          >
            <option value="">Leave unchanged</option>
            {(categories.data?.items ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {categoryLabel(category.name, category.emoji)}
              </option>
            ))}
          </select>
          <p id="bulk-category-hint" className="text-xs text-muted-foreground">
            {/*
              Said before the refusal rather than discovered after it. The
              category is the one field a filed period will not take, because it
              decides what is counted - and a person selecting a year of spend
              needs to know that before they press Apply, not from a report
              afterwards.
            */}
            A category decides what is counted, so it cannot change inside a filed period. Anything
            selected in one keeps the category it has, and the report says which.
          </p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <BulkTagField
          id="bulk-add-tags"
          label="Add tags"
          hint="Added to whatever each expense already carries."
          available={(tags.data?.items ?? []).filter((tag) => !addTagIds.includes(tag.id))}
          chosen={chosen(addTagIds)}
          onAdd={(id) => setAddTagIds((current) => [...current, id])}
          onRemove={(id) => setAddTagIds((current) => current.filter((held) => held !== id))}
        />
        <BulkTagField
          id="bulk-remove-tags"
          label="Remove tags"
          hint="Taken off the selected expenses. Their other tags are left alone."
          available={(tags.data?.items ?? []).filter((tag) => !removeTagIds.includes(tag.id))}
          chosen={chosen(removeTagIds)}
          onAdd={(id) => setRemoveTagIds((current) => [...current, id])}
          onRemove={(id) => setRemoveTagIds((current) => current.filter((held) => held !== id))}
        />
      </div>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" loading={pending} disabled={!acts}>
          Apply to {count}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * A tag chooser over the tags that already exist, and deliberately without the
 * create-a-tag path the row-level `TagPicker` has.
 *
 * Inventing a vocabulary is a considered act; doing it in the same keystroke
 * that applies it to four hundred records is how an org ends up with "travel",
 * "Travel " and "Travle" splitting one total three ways.
 */
function BulkTagField({
  id,
  label,
  hint,
  available,
  chosen,
  onAdd,
  onRemove,
}: {
  id: string;
  label: string;
  hint: string;
  available: TagOption[];
  chosen: TagOption[];
  onAdd: (tagId: string) => void;
  onRemove: (tagId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <select
        id={id}
        value=""
        aria-describedby={`${id}-hint`}
        onChange={(event) => {
          if (event.target.value) onAdd(event.target.value);
        }}
        className="h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
      >
        <option value="">{chosen.length === 0 ? 'None' : `${chosen.length} chosen`}</option>
        {available.map((tag) => (
          <option key={tag.id} value={tag.id}>
            {tag.name}
          </option>
        ))}
      </select>
      {chosen.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((tag) => (
            <ColourChip key={tag.id} colour={tag.colour} name={tag.name} onRemove={() => onRemove(tag.id)} />
          ))}
        </div>
      ) : null}
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {hint}
      </p>
    </div>
  );
}
